import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setActiveUserSkill,
  setUserSkills,
} from "../../store/slices/userSlice";
import { setIsButtonLoading } from "../../store/slices/loadingSlice";
import axios from "axios";
import { node_server } from "../../constants";
import useSkillsBar from "../useSkillsBar";

const useSkillsView = () => {
  const {
    value: user,
    userSkills,
    activeUserSkill,
  } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { activeSkill } = useSkillsBar();

  const sortSkills = (data) => {
    const MAX_SKILLS = 3;
    const updatedSkills = [];

    const createSkill = (skill, isActive = false) => {
      const isValidSkill = skill?.id && skill.id !== Infinity;

      return {
        ...skill,
        name: skill?.name,
        created: isValidSkill,
        ...(isValidSkill && { isActive }), // Add `isActive` only for valid skills
      };
    };

    let activeSkillFound = false; // Tracks if the active skill is set

    // Process each skill in the input
    for (let i = 0; i < MAX_SKILLS; i++) {
      const currentSkill = data[i];

      if (currentSkill && currentSkill.id !== Infinity) {
        const isActiveSkill =
          !activeSkillFound &&
          (currentSkill.id === activeSkill?.id || currentSkill.isActive);

        // Ensure only the matching skill is marked as active
        if (isActiveSkill) {
          activeSkillFound = true;
        }

        updatedSkills.push(createSkill(currentSkill, isActiveSkill));
      } else {
        // Add a placeholder skill for null or missing entries
        updatedSkills.push({
          id: null,
          name: `SKILL #0${i + 1}`,
          category: "notCreated",
          created: false,
        });
      }
    }

    // If no active skill matches the local storage ID, set the first valid skill as active
    if (!activeSkillFound) {
      const firstValidSkillIndex = updatedSkills.findIndex(
        (skill) => skill.id !== Infinity && skill.created
      );

      if (firstValidSkillIndex !== -1) {
        updatedSkills[firstValidSkillIndex].isActive = true;
        dispatch(setActiveUserSkill(updatedSkills[firstValidSkillIndex])); // Update state
      }
    } else {
      // Update the state with the currently active skill
      const activeSkillInState = updatedSkills.find((skill) => skill.isActive);
      if (activeSkillInState) {
        dispatch(setActiveUserSkill(activeSkillInState)); // Update state
      }
    }

    return updatedSkills;
  };

  useEffect(() => {
    localStorage.setItem(
      "active-skill",
      JSON.stringify({ id: activeUserSkill?.id ?? null })
    );
  }, [activeUserSkill]);

  useEffect(() => {
    if (userSkills.length === 3 && userSkills.includes(null)) {
      dispatch(setIsButtonLoading(true));
      const updatedSkills = sortSkills(userSkills);
      dispatch(setUserSkills(updatedSkills));
      dispatch(setIsButtonLoading(false));
    }
  }, [userSkills]);

  useEffect(() => {
    if (!user) return;
    const fetchSkills = async () => {
      dispatch(setIsButtonLoading(true));

      const { data, error } = await axios.get(
        `${node_server}/api/user/skills`,
        { withCredentials: true }
      );

      if (error) console.log(error);
      if (data) {
        dispatch(setIsButtonLoading(false));
        const updatedSkills = sortSkills(data);
        dispatch(setUserSkills(updatedSkills));
      }
    };

    if (userSkills.length === 0) fetchSkills();
    if (userSkills.length === 3) {
      dispatch(setIsButtonLoading(true));
      const updatedSkills = sortSkills(userSkills);

      dispatch(setUserSkills(updatedSkills));
      dispatch(setIsButtonLoading(false));
    }
  }, []);
};

export default useSkillsView;
